// Section 8: Approval / Rejection Letter.
//
// The Deal Outcome is computed server-side from the combined lane
// assessments. Letter Type defaults from OverallPass but is editable
// because the A1 may override it (e.g. issue Rejection even when
// lanes Pass). The server stores the type as sent on every save and
// records no override.
//
// DealOutcome must be FINALIZED before Section 8
// certifications can be signed. Until then, this panel surfaces:
//   1. an amber banner at the top with a primary "Finalize Deal
//      Outcome" CTA (visible only to users with IssueApprovalLetter);
//   2. a disabled state + tooltip on every cert checkbox that names the
//      remedy ("Finalize Deal Outcome above");
// Once finalized, the banner disappears, a green FINALIZED chip
// appears next to the Deal Outcome line, and the cert checkboxes
// become signable. The cert-sign backend guard remains as a defensive
// backstop; the new actionable error copy is what the global toast
// surfaces if it ever fires (concurrent-tab race, direct API call).

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { Button, Card, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { canIssueApprovalLetter } from "@/lib/dealFlowPermissions";
import {
  useDealOutcome,
  useFinalFinancialAddendum,
  useFinalizeDealOutcome,
  useUnderwritingCertifications,
  useUpsertFinalFinancialAddendum,
} from "@/hooks/dealFlow/useDealFlow";
import {
  useUndoDealOutcomeFinalize,
  useUndoS8CertSign,
} from "@/hooks/dealFlow/useAdminUndo";
import { UnderwritingCertificationCheckbox } from "../../common/UnderwritingCertificationCheckbox";
import { AdminUndoButton } from "../../admin/AdminUndoButton";
import type { DealOutcomeDto, LetterType } from "@/api/dealFlow/types";
import { calendarDateInputToIso } from "@/utils/datetime";
import { loanAmountInputValue, parseLoanAmountInput } from "./loanAmount";

const FINALIZE_REQUIRED_REASON =
  "Deal Outcome must be finalized first. Use the 'Finalize Deal Outcome' button above.";

const s8UndoConsequence = (
  <>
    This un-signs this certification by deleting its append-only audit
    row. If this cert was a prerequisite for the Section 9 hand-off,
    the server will refuse the undo until the hand-off is undone
    first. The Deal Outcome stays finalized.
  </>
);

interface ApprovalRejectionLetterSectionProps {
  dealId: string;
}

/** "Pass · Combined DQR 2". Before finalize the server sends no result until
 *  every non-N/A lane is issued (owner approval, 2026-10-13), and this says so. */
export function outcomeSummary(outcome: DealOutcomeDto): string {
  if (outcome.overallPass === null || outcome.combinedDealQualityRating === null)
    return "Pending · every non-N/A lane must be issued first";
  return `${outcome.overallPass ? "Pass" : "Fail"} · Combined DQR ${outcome.combinedDealQualityRating}`;
}

export function ApprovalRejectionLetterSection({
  dealId,
}: ApprovalRejectionLetterSectionProps) {
  const outcome = useDealOutcome(dealId);
  const addendum = useFinalFinancialAddendum(dealId);
  const upsert = useUpsertFinalFinancialAddendum(dealId);
  const finalize = useFinalizeDealOutcome(dealId);
  const s8Certs = useUnderwritingCertifications(dealId, "Underwriting", 8);
  const undoFinalize = useUndoDealOutcomeFinalize(dealId);
  const undoS8Cert = useUndoS8CertSign(dealId);
  const toast = useToast();
  const { user } = useAuth();
  const permissions = new Set(user?.permissions ?? []);
  const canFinalize = canIssueApprovalLetter(permissions);

  const isS8CertSigned = (code: string) =>
    !!(s8Certs.data ?? []).find((c) => c.certificationCode === code);

  const [letterType, setLetterType] = useState<LetterType>("Approval");
  const [letterIssuedAt, setLetterIssuedAt] = useState("");
  // The amount the loan boards at (owner ruling 6, 2026-10-08). Shown as the
  // server stored it (quantized to the cent) once saved.
  const [loanAmount, setLoanAmount] = useState("");
  const [loanAmountError, setLoanAmountError] = useState<string | null>(null);
  const [finalizeConfirmOpen, setFinalizeConfirmOpen] = useState(false);

  // Default LetterType from computed outcome on first load.
  useEffect(() => {
    if (addendum.data) {
      setLetterType(addendum.data.letterType);
      setLetterIssuedAt(toDateInputValue(addendum.data.letterIssuedAt));
      setLoanAmount(loanAmountInputValue(addendum.data.loanAmount));
    } else if (outcome.data && !outcome.data.isFinalized && outcome.data.overallPass !== null) {
      setLetterType(outcome.data.overallPass ? "Approval" : "Rejection");
    }
  }, [addendum.data, outcome.data]);

  const onSave = async () => {
    const parsedAmount = parseLoanAmountInput(loanAmount);
    if (parsedAmount.kind === "invalid") {
      setLoanAmountError(parsedAmount.error);
      return;
    }
    setLoanAmountError(null);
    try {
      await upsert.mutateAsync({
        letterType,
        letterIssuedAt: calendarDateInputToIso(letterIssuedAt),
        // Omitted when blank: the save leaves a stored amount alone.
        ...(parsedAmount.kind === "amount" ? { loanAmount: parsedAmount.amount } : {}),
      });
      toast.success("Letter saved.");
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not save.",
      );
    }
  };

  const onConfirmFinalize = async () => {
    try {
      await finalize.mutateAsync();
      setFinalizeConfirmOpen(false);
      toast.success("Deal Outcome finalized.");
    } catch (err) {
      // 409 = a concurrent tab beat us to the finalize. Treat as
      // soft-success: the outcome cache is refetched by the mutation
      // hook's invalidation, the banner disappears, and the user sees
      // a calm confirmation rather than a hostile error.
      if (err instanceof ApiError && err.status === 409) {
        await outcome.refetch();
        setFinalizeConfirmOpen(false);
        toast.success("Deal Outcome was already finalized.");
        return;
      }
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not finalize Deal Outcome.",
      );
    }
  };

  const isFinalized = !!outcome.data?.isFinalized;
  const showFinalizeBanner = !!outcome.data && !isFinalized;

  return (
    <Card>
      <header className="mb-space-3">
        <h3 className="font-display text-h4 text-onyx m-0">
          Section 8 — Approval / Rejection Letter
        </h3>
        <p className="text-caption text-gray-mid m-0">
          Letter type defaults from the computed Deal Outcome.
        </p>
      </header>

      {showFinalizeBanner && (
        <div
          role="alert"
          data-testid="section8-finalize-banner"
          className="rounded-md border-l-4 border-status-warning bg-status-warning/10 px-space-3 py-space-3 mb-space-3 flex items-start justify-between gap-space-3"
        >
          <div className="flex-1">
            <p className="font-display text-small text-onyx m-0">
              Deal Outcome must be finalized before Section 8
              certifications can be signed.
            </p>
            <p className="text-caption text-gray-mid mt-space-1 m-0">
              Finalization locks the computed outcome
              {outcome.data && (
                <>
                  {" "}
                  ({outcomeSummary(outcome.data)})
                </>
              )}
              {" "}to a permanent audit record. This action is one-way.
            </p>
          </div>
          {canFinalize && (
            <Button
              variant="primary"
              onClick={() => setFinalizeConfirmOpen(true)}
              disabled={finalize.isPending}
            >
              {finalize.isPending ? "Finalizing…" : "Finalize Deal Outcome →"}
            </Button>
          )}
        </div>
      )}

      {outcome.data && (
        <div className="rounded-md border border-border-light bg-white-warm px-space-3 py-space-2 mb-space-3">
          <p className="text-small text-onyx m-0 flex items-center gap-space-2">
            <span>
              <strong>
                Deal Outcome ({isFinalized ? "finalized" : "computed"})
              </strong>
              :{" "}
              {outcomeSummary(outcome.data)}
            </span>
            {isFinalized && (
              <span
                data-testid="section8-finalized-chip"
                className="inline-flex items-center px-space-2 py-[2px] rounded-full text-caption font-display uppercase tracking-label bg-status-success/15 text-status-success"
              >
                Finalized
              </span>
            )}
            {isFinalized && (
              <AdminUndoButton
                label="Finalize"
                modalTitle="Undo Deal Outcome Finalize"
                modalConsequence={
                  <>
                    This returns the Deal Outcome to Computed state. Section
                    8 certification signs are NOT auto-undone; the server
                    will refuse this undo until any signed S8 certs are
                    individually undone first.
                  </>
                }
                successToast="Deal Outcome finalize undone. Outcome returned to Computed."
                invoke={(reason) => undoFinalize.mutateAsync(reason)}
              />
            )}
          </p>
        </div>
      )}

      <fieldset className="mb-space-3 m-0 p-0 border-0">
        <legend className="font-display text-label uppercase tracking-label text-gold-shadow mb-space-2">
          Letter type
        </legend>
        <div className="flex gap-space-3">
          <label className="flex items-center gap-space-2 text-small">
            <input
              type="radio"
              name="letter-type"
              checked={letterType === "Approval"}
              onChange={() => setLetterType("Approval")}
            />
            Approval letter
          </label>
          <label className="flex items-center gap-space-2 text-small">
            <input
              type="radio"
              name="letter-type"
              checked={letterType === "Rejection"}
              onChange={() => setLetterType("Rejection")}
            />
            Rejection letter
          </label>
        </div>
      </fieldset>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 mb-space-3">
        <FormField label="Letter issued date">
          <Input
            type="date"
            value={letterIssuedAt}
            onChange={(e) => setLetterIssuedAt(e.target.value)}
          />
        </FormField>
        <FormField
          label="Loan Amount (USD)"
          helperText="The amount the loan boards at. Required before Create Loan."
          error={loanAmountError ?? undefined}
        >
          <Input
            type="text"
            inputMode="decimal"
            value={loanAmount}
            onChange={(e) => {
              setLoanAmount(e.target.value);
              setLoanAmountError(null);
            }}
            data-testid="section8-loan-amount"
          />
        </FormField>
      </div>

      <div className="flex justify-end mb-space-4">
        <Button variant="secondary" onClick={() => void onSave()} disabled={upsert.isPending}>
          {upsert.isPending ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="border-t border-border-light pt-space-3">
        <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
          <li>
            <UnderwritingCertificationCheckbox
              dealId={dealId}
              tab="Underwriting"
              section={8}
              certificationCode="UW.S8.LetterIssued"
              label={`I confirm the ${letterType.toLowerCase()} letter has been issued to the borrower.`}
              disabled={!isFinalized}
              disabledReason={!isFinalized ? FINALIZE_REQUIRED_REASON : undefined}
            />
            {isS8CertSigned("UW.S8.LetterIssued") && (
              <div className="pl-[34px] mt-space-1">
                <AdminUndoButton
                  label="Sign"
                  modalTitle="Undo Section 8 Letter Issuance"
                  modalConsequence={s8UndoConsequence}
                  successToast="Section 8 certification un-signed."
                  invoke={(reason) =>
                    undoS8Cert.mutateAsync({
                      certificationCode: "UW.S8.LetterIssued",
                      reason,
                    })
                  }
                />
              </div>
            )}
          </li>
        </ul>
        <Link
          to={`/deal-flow/deals/${dealId}?tab=documents`}
          className="mt-space-3 inline-flex items-center gap-space-1 text-label text-gold-shadow hover:underline"
        >
          <ExternalLink size={12} aria-hidden /> View Documents tab
        </Link>
      </div>

      <Modal
        open={finalizeConfirmOpen}
        onClose={() => !finalize.isPending && setFinalizeConfirmOpen(false)}
        title="Finalize Deal Outcome?"
        description="Once finalized, this entry is captured in the audit log and cannot be undone outside of an admin reset."
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setFinalizeConfirmOpen(false)}
              disabled={finalize.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void onConfirmFinalize()}
              disabled={finalize.isPending}
            >
              {finalize.isPending ? "Finalizing…" : "CONFIRM: Finalize"}
            </Button>
          </>
        }
      >
        <p className="text-small text-onyx m-0">
          You are about to finalize the Deal Outcome for this deal:
        </p>
        {outcome.data && (
          <p className="font-display text-small text-onyx mt-space-2 m-0">
            Outcome:{" "}
            <strong>{outcomeSummary(outcome.data)}</strong>
          </p>
        )}
        <p className="text-small text-gray-mid mt-space-2 m-0">
          Section 8 certifications will become available to sign once
          finalization completes.
        </p>
      </Modal>
    </Card>
  );
}

function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}
